import React, { useCallback, useEffect, useState } from 'react';
import { Accordion, Alert, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getPoliciesRetentionAPI, updatePoliciesRetentionAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import ConfirmModal from '../../../Components/shared/ConfirmModal';
import { companyUrl } from '../../../utils/mhNav';

export default function PoliciesRetention() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [p, setP] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPoliciesRetentionAPI();
      setP(res.data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const minErr = (field, value) => {
    const m = p?.platformMinimums?.[field];
    if (m == null || value == null) return null;
    if (Number(value) < m) return `Minimum is ${m} days per platform policy`;
    return null;
  };

  if (error && !p) {
    return (
      <div>
        <PageHeader title="Policies" breadcrumbs={[{ label: 'Company', path: companyUrl('settings') }]} />
        <Button onClick={load}>Retry</Button>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Policies & retention"
        breadcrumbs={[
          { label: 'Company', path: companyUrl('dashboard') },
          { label: 'Settings', path: companyUrl('settings') },
          { label: 'Policies' },
        ]}
      />
      <Alert variant="info" className="mb-3">
        These settings operate within platform-defined minimums.
      </Alert>
      {loading || !p ? (
        <LoadingSkeleton rows={6} cols={2} />
      ) : (
        <>
          <Accordion defaultActiveKey="0">
            <Accordion.Item eventKey="0">
              <Accordion.Header>Retention</Accordion.Header>
              <Accordion.Body>
                <Form.Group className="mb-2">
                  <Form.Label>Transcripts (days)</Form.Label>
                  <Form.Control type="number" value={p.transcriptDays} onChange={(e) => setP({ ...p, transcriptDays: Number(e.target.value) })} />
                  {minErr('transcriptDays', p.transcriptDays) && <div className="text-danger small">{minErr('transcriptDays', p.transcriptDays)}</div>}
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>MOM documents (days)</Form.Label>
                  <Form.Control type="number" value={p.momDays} onChange={(e) => setP({ ...p, momDays: Number(e.target.value) })} />
                  {minErr('momDays', p.momDays) && <div className="text-danger small">{minErr('momDays', p.momDays)}</div>}
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Meeting recordings (days)</Form.Label>
                  <Form.Control type="number" value={p.recordingDays} onChange={(e) => setP({ ...p, recordingDays: Number(e.target.value) })} />
                  {minErr('recordingDays', p.recordingDays) && <div className="text-danger small">{minErr('recordingDays', p.recordingDays)}</div>}
                </Form.Group>
                <Form.Group>
                  <Form.Label>Audit logs (days)</Form.Label>
                  <Form.Control type="number" value={p.auditDays} onChange={(e) => setP({ ...p, auditDays: Number(e.target.value) })} />
                  {minErr('auditDays', p.auditDays) && <div className="text-danger small">{minErr('auditDays', p.auditDays)}</div>}
                </Form.Group>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="1">
              <Accordion.Header>Privacy</Accordion.Header>
              <Accordion.Body>
                <Form.Check type="switch" label="PII masking in transcripts" checked={p.piiMasking} onChange={(e) => setP({ ...p, piiMasking: e.target.checked })} className="mb-2" />
                <Form.Group className="mb-2">
                  <Form.Label>Guest attendee data retention (days)</Form.Label>
                  <Form.Control type="number" value={p.guestRetentionDays} onChange={(e) => setP({ ...p, guestRetentionDays: Number(e.target.value) })} />
                </Form.Group>
                <Form.Check type="switch" label="Allow attendees to download their MOM" checked={p.allowMomDownload} onChange={(e) => setP({ ...p, allowMomDownload: e.target.checked })} />
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="2">
              <Accordion.Header>Legal notices</Accordion.Header>
              <Accordion.Body>
                <Form.Group className="mb-2">
                  <Form.Label>Legal footer (MOM exports)</Form.Label>
                  <Form.Control as="textarea" rows={2} value={p.legalFooter} onChange={(e) => setP({ ...p, legalFooter: e.target.value })} />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Disclaimer</Form.Label>
                  <Form.Control as="textarea" rows={2} value={p.disclaimer} onChange={(e) => setP({ ...p, disclaimer: e.target.value })} />
                </Form.Group>
                <Form.Check type="switch" label="Require legal acknowledgment on MOM approval" checked={p.requireLegalAckOnApproval} onChange={(e) => setP({ ...p, requireLegalAckOnApproval: e.target.checked })} />
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="3">
              <Accordion.Header>Data export</Accordion.Header>
              <Accordion.Body>
                <Form.Check type="switch" label="Allow users to export their meeting data" checked={p.allowUserDataExport} onChange={(e) => setP({ ...p, allowUserDataExport: e.target.checked })} className="mb-2" />
                <div className="small text-muted mb-1">Export formats</div>
                {['CSV', 'XLSX', 'PDF'].map((fmt) => (
                  <Form.Check
                    key={fmt}
                    type="checkbox"
                    label={fmt}
                    checked={(p.exportFormats || []).includes(fmt)}
                    onChange={(e) => {
                      const next = new Set(p.exportFormats || []);
                      if (e.target.checked) next.add(fmt);
                      else next.delete(fmt);
                      setP({ ...p, exportFormats: [...next] });
                    }}
                    className="mb-1"
                  />
                ))}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          <Button className="mt-3" onClick={() => setConfirmOpen(true)}>Save</Button>
        </>
      )}

      <ConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Apply policy changes?"
        message="These policy changes will apply to all future operations."
        confirmLabel="Save policies"
        onConfirm={async () => {
          await updatePoliciesRetentionAPI(p);
          toast.success('Policies saved');
          setConfirmOpen(false);
          load();
        }}
      />
    </div>
  );
}
